import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';

interface SEOProps {
    title: string;
    description: string;
    canonicalUrl?: string;
    ogImage?: string;
    ogType?: string;
    keywords?: string;
    jsonLd?: object | object[];
    articleAuthor?: string;
}

const SITE_NAME = 'Asim Hafeez';
const DEFAULT_OG_IMAGE = '/ss.png';
const BASE_URL = 'https://asimhafeez.com';

/**
 * Generate a BreadcrumbList JSON-LD schema for navigation breadcrumbs.
 * Helps search engines understand site hierarchy and display breadcrumbs in SERPs.
 */
export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": item.name,
        "item": item.url
    }))
});

const SEO: React.FC<SEOProps> = ({
    title,
    description,
    canonicalUrl,
    ogImage = DEFAULT_OG_IMAGE,
    ogType = 'website',
    keywords,
    jsonLd,
    articleAuthor,
}) => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const fullCanonical = canonicalUrl ? `${BASE_URL}${canonicalUrl}` : BASE_URL;
    const fullOgImage = ogImage.startsWith('http') ? ogImage : `${BASE_URL}${ogImage}`;

    const jsonLdArray = useMemo(
        () => (jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []),
        [jsonLd]
    );
    const serializedJsonLd = useMemo(
        () => jsonLdArray.map((schema) => JSON.stringify(schema)),
        [jsonLdArray]
    );

    useEffect(() => {
        if (!serializedJsonLd.length) {
            return;
        }

        const injectedScripts = serializedJsonLd.map((schemaText) => {
            const script = document.createElement('script');
            script.type = 'application/ld+json';
            script.setAttribute('data-seo-jsonld', 'true');
            script.textContent = schemaText;
            document.head.appendChild(script);
            return script;
        });

        return () => {
            injectedScripts.forEach((script) => script.remove());
        };
    }, [serializedJsonLd]);

    return (
        <>
            <Helmet>
                {/* Primary Meta Tags */}
                <title>{fullTitle}</title>
                <meta name="description" content={description} />
                {keywords && <meta name="keywords" content={keywords} />}
                <meta name="author" content="Asim Hafeez" />
                <link rel="canonical" href={fullCanonical} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content={ogType} />
                <meta property="og:url" content={fullCanonical} />
                <meta property="og:title" content={fullTitle} />
                <meta property="og:description" content={description} />
                <meta property="og:image" content={fullOgImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:site_name" content={SITE_NAME} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={fullTitle} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={fullOgImage} />
                <meta name="twitter:creator" content="@asimhafeezz" />

                {/* Article-specific meta tags */}
                {ogType === 'article' && articleAuthor && (
                    <meta property="article:author" content={articleAuthor} />
                )}
            </Helmet>
        </>
    );
};

export default SEO;
